/**
 * §11.5 particle system — four instanced quad pools (combat sparks, explosion, ambient
 * motes, status loops), 3,000 live hard cap, additive, budgeted per §11.4.
 *
 * Spawn policy is §11.5 verbatim: at ≥ 90% pool occupancy decorative emitters halve
 * their rate, at 100% decorative spawns are dropped outright, and gameplay-cued
 * particles (telegraphs, deaths, hits) always spawn by recycling the oldest slot in
 * pool-index order. Every emitter consumes only the `vfx` RNG stream (spine §7).
 *
 * Storage is struct-of-arrays with a ring cursor. Nothing here allocates after boot.
 */

import * as THREE from 'three';
import type { HitContact } from '../core/events';
import type { RngStream } from '../core/rng';
import { wrapDeltaX, wrapDeltaY, wrapX, wrapY } from '../sim/wrap';
import { InstancedBatch, LAYER_PARTICLE, MODE_BEAM, MODE_SHOCK, MODE_SPRITE } from './instanced';
import { quality } from './quality';
import { buildBeamGeometry, buildDiscGeometry, buildQuadGeometry } from './silhouettes';

export const POOL_SPARK = 0;
export const POOL_EXPLOSION = 1;
export const POOL_AMBIENT = 2;
export const POOL_STATUS = 3;
/** AMD-6 D7: line-segment shards of the dying entity's own geometry. */
export const POOL_SHARD = 4;
/** AMD-6 D7: the expanding 2 px shockwave ring. */
export const POOL_SHOCK = 5;
const POOL_COUNT = 6;

/** §11.5: 3,000 global cap split across the pools. */
const POOL_CAPACITY: readonly number[] = [1000, 800, 600, 300, 240, 60];

/**
 * AMD-7 R2b — velocity stretch.
 *
 * Every sprite particle was an axis-aligned disc, so a 24 u/s spark and a 2 u/s mote
 * drew the same mark and nothing in a burst carried a direction. A particle is now
 * scaled along its own velocity by `1 + clamp(speed / REF, 0, 1.5)` with the minor axis
 * held constant, so the quad elongates and the falloff turns elliptical without the
 * particle gaining any area across its path.
 */
const STRETCH_REF_SPEED = 9;
const STRETCH_MAX = 1.5;

/**
 * Impact-beat sizing. The flash is drawn with a radius of `clamp(bodyRadius, MIN, MAX) *
 * SCALE`, at the anchor `emitImpact` picks.
 *
 * MIN is what makes the beat read on a common body. At the 390×844 framing one world
 * unit is 20 px, so a 0.55 u driftfin is a 22 px disc and the bolt that kills it goes
 * out 31 px from its centre — one whole tick of travel short of the rim, because a
 * projectile is released on the tick its hit resolves and is never drawn again. A flash
 * clamped up to 0.7 u reaches 22 px from the centre, so it covers the body AND closes
 * that gap: the shot goes out and the body lights up in the same place.
 *
 * MAX is what stops a boss from strobing. A 2.5 u boss scaled straight through would
 * take a 67 px flash on every one of the many hits a second it absorbs; clamped to
 * 1.05 u it takes a 28 px bloom at its core instead.
 */
const IMPACT_R_MIN = 0.7;
const IMPACT_R_MAX = 1.05;
const IMPACT_FLASH_SCALE = 1.6;
/**
 * Flash life, seconds — four frames for a hit, six for a crit. Short on purpose: the
 * beat has to be over before the next shot lands, or a sustained stream of fire welds
 * into one standing glow and the per-hit read is gone again.
 */
const IMPACT_FLASH_LIFE = 0.075;
const IMPACT_FLASH_LIFE_CRIT = 0.105;

/**
 * Anchoring threshold: at or below this body radius the beat is drawn at the centre, above
 * it at the contact point.
 *
 * Measured, not chosen. `reports/impact/flash-profile.mjs` emits one flash per candidate
 * radius onto an empty stage at the 390×844 framing and `flash-profile.py` reads its warm
 * radial profile with the same `R − B > 8` floor `measure.py` uses. The flash's reach grows
 * with the body until the size clamp binds and then saturates at 32 px:
 *
 *   body r (u)   0.55   0.90   1.20   1.40   1.60   1.68   2.00   2.50
 *   reach (px)     22     27     32     32     32     32     32     32
 *   cover        1.99   1.50   1.33   1.14   1.00   0.95   0.80   0.64
 *
 * Coverage crosses 1.0 at 32 px / 20.05 px-per-unit = 1.596 u. Below that the flash blankets
 * the silhouette, so centre and contact point are the same mark and the centre is the
 * steadier one — a body being shot from two sides gets one beat, not a flicker between rims.
 * Above it the flash is a bloom smaller than the thing it marks, and drawing it at the centre
 * of a 2.5 u boss puts it 42 px from the shot that caused it, which reads as the boss's core
 * pulsing on its own schedule.
 *
 * Every archetype in `enemies.json` is ≤ 1.3 u and stays centred; the bodies that cross are
 * elite luremaw (1.65 u), elite brood-bell (1.95 u) and all four bosses (2.5–7.5 u).
 */
const IMPACT_ANCHOR_R_MAX = 1.6;
/**
 * How far inside the rim the contact-anchored flash sits, in flash radii.
 *
 * Also measured. `measure.py` over the boss capture, on the hit frame — warm energy inside
 * the hitbox, warm energy outside it, and the warm centroid's offset from the centre:
 *
 *   inset          E_body   E_out   centroid
 *   centre (old)      4.8     0.0     −3 px      the core pulsing, 42 px off the shot
 *   0.0              18.3    28.8    +42 px      61% of the light burning in open water
 *   0.5              19.3     0.0    +31 px      all of it on the body, mark on the rim
 *   1.0               8.8     0.0    +11 px      back toward the core, and washed out
 *
 * 0.5 puts the whole beat on the struck body while its mass still sits at three quarters of
 * the body radius toward the rim the shot came from.
 */
const IMPACT_CONTACT_INSET = 0.5;

/**
 * Spark cone. `SPARK_CONE` is the ±0.8 rad spread every burst has always had around its own
 * axis. `SPARK_SPALL_*` is the impact-only back lobe (see `emitSparks`).
 */
const SPARK_CONE = 1.6;
const SPARK_SPALL_CONE = 2.0;
const SPARK_SPALL_SPEED = 0.55;
/**
 * Fraction of an impact burst thrown back along the incoming vector. A real strike sprays
 * spall both ways, so a pure forward jet reads as a jet rather than a hit; an even split
 * reads as an explosion and loses the direction the shot was travelling. At 0.25 the standard
 * 4-spark burst sends one back and the 8-spark crit two, and the burst reads as a
 * through-and-through with debris coming off the entry face.
 */
const IMPACT_SPALL_FRAC = 0.25;

/** Motion codes. §11.2.5 double-codes every status by shape AND motion. */
export const MOTION_BALLISTIC = 0;
export const MOTION_RISE = 1;
export const MOTION_FALL = 2;
export const MOTION_TWINKLE = 3;
export const MOTION_INWARD = 4;
/** AMD-6 D7: velocity-aligned segment with drag, its own heading as rotation. */
export const MOTION_SHARD = 5;
/** AMD-6 D7: radius sweeps 0 → `size` over the particle's life. */
export const MOTION_SHOCK = 6;

/**
 * Death styles. The AMD-6 D7 beat (flash → shards → ring) is what makes a kill read as a
 * kill, so every style keeps it; what varies is the character, because a swarm dying at
 * four a second with one animation reads as one repeated asset rather than four deaths.
 * Rolled from the `vfx` stream, which is presentation-only — no gameplay draw moves.
 */
/** 0 is the plain burst — the original beat, and the one no branch has to name. */
const STYLE_CLEAVE = 1;
const STYLE_COLLAPSE = 2;
const STYLE_FIZZLE = 3;

class Pool {
  readonly capacity: number;
  readonly x: Float32Array;
  readonly y: Float32Array;
  readonly vx: Float32Array;
  readonly vy: Float32Array;
  readonly life: Float32Array;
  readonly maxLife: Float32Array;
  readonly size: Float32Array;
  /** Second scale component: shard half-width, ring thickness. */
  readonly size2: Float32Array;
  readonly color: Int32Array;
  readonly motion: Int8Array;
  readonly gameplay: Uint8Array;
  readonly emissive: Float32Array;
  cursor = 0;
  alive = 0;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.x = new Float32Array(capacity);
    this.y = new Float32Array(capacity);
    this.vx = new Float32Array(capacity);
    this.vy = new Float32Array(capacity);
    this.life = new Float32Array(capacity);
    this.maxLife = new Float32Array(capacity);
    this.size = new Float32Array(capacity);
    this.size2 = new Float32Array(capacity);
    this.color = new Int32Array(capacity);
    this.motion = new Int8Array(capacity);
    this.gameplay = new Uint8Array(capacity);
    this.emissive = new Float32Array(capacity);
  }
}

export class ParticleSystem {
  private readonly pools: Pool[] = [];
  private readonly batches: InstancedBatch[] = [];
  private droppedThisFrame = 0;

  constructor(scene: THREE.Scene) {
    const quad = buildQuadGeometry();
    const beam = buildBeamGeometry();
    const disc = buildDiscGeometry(48);
    for (let i = 0; i < POOL_COUNT; i++) {
      this.pools.push(new Pool(POOL_CAPACITY[i]));
      const geometry = i === POOL_SHARD ? beam : i === POOL_SHOCK ? disc : quad;
      const mode = i === POOL_SHARD ? MODE_BEAM : i === POOL_SHOCK ? MODE_SHOCK : MODE_SPRITE;
      const batch = new InstancedBatch(geometry, POOL_CAPACITY[i], {
        mode,
        blend: 'add',
        renderOrder: LAYER_PARTICLE + i,
        name: `particles-${i}`,
      });
      scene.add(batch.mesh);
      this.batches.push(batch);
    }
  }

  /** Live particles across all four pools. Feeds `counters.particles_alive` checks. */
  get aliveCount(): number {
    let n = 0;
    for (let i = 0; i < POOL_COUNT; i++) n += this.pools[i].alive;
    return n;
  }

  /** Decorative spawns refused this frame by the §11.5 policy. */
  get dropped(): number {
    return this.droppedThisFrame;
  }

  /**
   * Claims a slot. Gameplay-cued particles always get one; decorative particles obey
   * the §11.5 occupancy rule. Returns -1 when the spawn is refused.
   */
  private claim(pool: Pool, gameplay: boolean, vfx: RngStream): number {
    const budget = Math.min(pool.capacity, Math.max(1, Math.round((pool.capacity * quality.particleCap) / 3000)));
    const occupancy = pool.alive / budget;
    if (!gameplay) {
      if (occupancy >= 1) {
        this.droppedThisFrame++;
        return -1;
      }
      if (occupancy >= 0.9 && vfx.next() < 0.5) {
        this.droppedThisFrame++;
        return -1;
      }
    }
    // Ring cursor: recycling always takes the oldest slot in pool-index order (§14.3).
    const i = pool.cursor;
    pool.cursor = i + 1 >= budget ? 0 : i + 1;
    if (pool.life[i] <= 0) pool.alive++;
    return i;
  }

  private write(
    pool: Pool,
    i: number,
    x: number,
    y: number,
    vx: number,
    vy: number,
    life: number,
    size: number,
    color: number,
    motion: number,
    emissive: number,
    gameplay: boolean,
    size2 = 0,
  ): void {
    pool.x[i] = x;
    pool.y[i] = y;
    pool.vx[i] = vx;
    pool.vy[i] = vy;
    pool.life[i] = life;
    pool.maxLife[i] = life;
    pool.size[i] = size;
    pool.size2[i] = size2;
    pool.color[i] = color;
    pool.motion[i] = motion;
    pool.emissive[i] = emissive;
    pool.gameplay[i] = gameplay ? 1 : 0;
  }

  /**
   * `vfx.hit-standard` / `vfx.hit-crit`: a short directional spark burst along `dirX, dirY`.
   * Counts scale with `quality.sparkScale` only when the burst is decorative.
   *
   * `spallFrac` splits the burst: that fraction of the sparks goes backwards along the axis
   * instead of forwards, in a wider, slower lobe. At 0 — every caller but the impact beat —
   * the angle is `atan2(dir) + (u − 0.5) × SPARK_CONE` and the speed `speed × (0.5 + u)`,
   * exactly as before, so the dash, the intake burst and the mote pop draw the same marks
   * from the same four `vfx` values per spark. The split reuses the angle draw rather than
   * adding one, so the stream's draw order does not move either.
   */
  emitSparks(
    x: number,
    y: number,
    dirX: number,
    dirY: number,
    count: number,
    color: number,
    vfx: RngStream,
    gameplay = true,
    speed = 9,
    emissive = 1.4,
    spallFrac = 0,
  ): void {
    const pool = this.pools[POOL_SPARK];
    const n = gameplay ? count : Math.max(1, Math.round(count * quality.decorativeRate));
    const axis = Math.atan2(dirY, dirX);
    for (let k = 0; k < n; k++) {
      const i = this.claim(pool, gameplay, vfx);
      if (i < 0) return;
      const u = vfx.next();
      let a: number;
      let scale: number;
      if (u < spallFrac) {
        a = axis + Math.PI + (u / spallFrac - 0.5) * SPARK_SPALL_CONE;
        scale = SPARK_SPALL_SPEED;
      } else {
        a = axis + ((u - spallFrac) / (1 - spallFrac) - 0.5) * SPARK_CONE;
        scale = 1;
      }
      const sp = speed * scale * (0.5 + vfx.next());
      this.write(
        pool, i, x, y,
        Math.cos(a) * sp, Math.sin(a) * sp,
        0.18 + vfx.next() * 0.22,
        0.11 + vfx.next() * 0.07,
        color, MOTION_BALLISTIC, emissive, gameplay,
      );
    }
  }

  /**
   * `vfx.hit-standard` / `vfx.hit-crit` — the impact beat.
   *
   * A hit used to be four 3 px sparks at the centre and nothing else. Frame captures of a
   * single impact at the phone framing (`reports/impact/`) show why that never read: the
   * sparks are correct — they spawn 1.7–3.4 px from the centre — but against a bloomed
   * 22 px body they are invisible, while the bolt that caused the hit carries a halo an
   * order of magnitude brighter and blinks out 31 px short of the centre, a full tick of
   * travel from the rim. The brightest impact-adjacent thing on screen was the shot
   * ending in empty space, so the hit read at the edge of the body rather than on it.
   *
   * The beat is a body-scaled core flash plus directional sparks leaving from the same
   * point. The flash is the mark the eye lands on; the sparks carry the energy outward
   * across the rim, forwards along the shot with a quarter of them thrown back as spall.
   *
   * Anchor: the body centre while the flash still covers the silhouette, the contact point
   * once it cannot (`IMPACT_ANCHOR_R_MAX`). `contact` is `null` for every source that has no
   * contact point — a burn tick, a zone, a beam sheet — and those always anchor at the centre
   * and take the caller's fallback axis for the spark direction.
   *
   * One pooled slot per hit, in the explosion pool. Censused over 30 s of the §C.1 stress
   * seed at 19 minutes (`reports/impact/pool-budget.mjs`) the field lands 0.2 hits/frame
   * on average and 12 on its worst frame, so a 4.5-frame flash costs ≤ 54 live slots
   * against the 240-slot handheld budget — it saturates at 53 hits/frame, four times the
   * worst frame measured, so nothing is capped and the death bursts sharing this pool are
   * not starved. Gameplay-cued, so it is exempt from the §14.6 ladder.
   */
  emitImpact(
    x: number,
    y: number,
    bodyRadius: number,
    contact: HitContact | null,
    fallbackDirX: number,
    fallbackDirY: number,
    count: number,
    color: number,
    vfx: RngStream,
    crit: boolean,
  ): void {
    const r = bodyRadius < IMPACT_R_MIN ? IMPACT_R_MIN : bodyRadius > IMPACT_R_MAX ? IMPACT_R_MAX : bodyRadius;
    let ax = x;
    let ay = y;
    let dirX = fallbackDirX;
    let dirY = fallbackDirY;
    if (contact !== null) {
      dirX = contact.dir_x;
      dirY = contact.dir_y;
      if (bodyRadius > IMPACT_ANCHOR_R_MAX) {
        // AMD-24 — the contact point can sit across the seam from the centre, so the offset
        // is a wrapped delta and the anchor is canonicalised back onto the torus. A raw
        // subtraction here puts the flash on the far side of the map.
        const dx = wrapDeltaX(contact.x - x);
        const dy = wrapDeltaY(contact.y - y);
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d > 1e-6) {
          const inside = (d < bodyRadius ? d : bodyRadius) - r * IMPACT_FLASH_SCALE * IMPACT_CONTACT_INSET;
          const t = (inside > 0 ? inside : 0) / d;
          ax = wrapX(x + dx * t);
          ay = wrapY(y + dy * t);
        }
      }
    }
    const pool = this.pools[POOL_EXPLOSION];
    const i = this.claim(pool, true, vfx);
    if (i >= 0) {
      this.write(
        pool, i, ax, ay, 0, 0,
        crit ? IMPACT_FLASH_LIFE_CRIT : IMPACT_FLASH_LIFE,
        r * IMPACT_FLASH_SCALE,
        color, MOTION_BALLISTIC, crit ? 3.1 : 2.6, true,
      );
    }
    this.emitSparks(ax, ay, dirX, dirY, count, color, vfx, true, crit ? 13 : 9, 1.4, IMPACT_SPALL_FRAC);
  }

  /** `vfx.death-*`: a radial burst. Spark counts halve at §14.6 rung 2. */
  emitExplosion(
    x: number,
    y: number,
    radius: number,
    count: number,
    color: number,
    vfx: RngStream,
    gameplay = true,
  ): void {
    const pool = this.pools[POOL_EXPLOSION];
    const n = Math.max(1, Math.round(count * (gameplay ? quality.sparkScale : quality.decorativeRate * quality.sparkScale)));
    for (let k = 0; k < n; k++) {
      const i = this.claim(pool, gameplay, vfx);
      if (i < 0) return;
      const a = vfx.next() * Math.PI * 2;
      const sp = radius * (2.4 + vfx.next() * 3.2);
      this.write(
        pool, i,
        x + Math.cos(a) * radius * 0.25,
        y + Math.sin(a) * radius * 0.25,
        Math.cos(a) * sp, Math.sin(a) * sp,
        0.3 + vfx.next() * 0.35,
        0.14 + vfx.next() * 0.16 * radius,
        color, MOTION_BALLISTIC, 1.6, gameplay,
      );
    }
  }

  /**
   * AMD-6 D7 — the destruction beat, replacing the cyan confetti scatter.
   *
   * Three gameplay-cued layers, all of them exempt from the §14.6 ladder because a kill
   * that reads as a kill is the feedback the whole loop runs on:
   *  - a one-frame white core flash at a slightly dilated silhouette,
   *  - 6–10 line-segment shards at 220–380 px/s with a 0.45 s life, each carrying its
   *    own 4-sample taper via the beam profile,
   *  - a 2 px ring sweeping 0 → 90 px over 180 ms at alpha 0.9 → 0.
   *
   * `radius` is the entity's world radius; `pxPerUnit` converts the art direction's
   * pixel figures at the 1080p reference framing.
   */
  emitDestruction(
    x: number,
    y: number,
    radius: number,
    ringRadius: number,
    coreToken: number,
    hueToken: number,
    count: number,
    vfx: RngStream,
  ): void {
    // One of four deaths, rolled per kill. The beat is fixed; its character is not.
    const style = (vfx.next() * 4) | 0;
    const cleave = style === STYLE_CLEAVE;
    const collapse = style === STYLE_COLLAPSE;
    const fizzle = style === STYLE_FIZZLE;

    const flashPool = this.pools[POOL_EXPLOSION];
    const fi = this.claim(flashPool, true, vfx);
    if (fi >= 0) {
      // A collapse flashes late and small; a cleave flashes hard.
      const fScale = collapse ? 0.8 : cleave ? 1.35 : 1.15;
      const fLife = fizzle ? 0.035 : 0.05;
      this.write(flashPool, fi, x, y, 0, 0, fLife, radius * fScale, coreToken, MOTION_BALLISTIC, 3.4, true);
    }

    const shards = this.pools[POOL_SHARD];
    // Fizzle throws fewer pieces, cleave throws them all one way, so it can spare more.
    const scaled = fizzle ? count * 0.6 : cleave ? count * 1.15 : count;
    const n = Math.max(4, Math.min(10, Math.round(scaled)));
    // Where the spray points and how wide it opens. A burst is the full circle; a cleave
    // is a 70° wedge about a random axis, which is what makes it read as a cut rather
    // than a pop.
    const axis = vfx.next() * Math.PI * 2;
    const arc = cleave ? 1.22 : Math.PI * 2;
    const spread = collapse ? 0.55 : 1;
    for (let k = 0; k < n; k++) {
      const i = this.claim(shards, true, vfx);
      if (i < 0) break;
      const a = cleave
        ? axis + (((k + vfx.next() * 0.6) / n) - 0.5) * arc
        : axis + ((k + vfx.next() * 0.7) / n) * arc;
      // Collapse starts slow and outside, so the silhouette folds before it lets go.
      const sp = (collapse ? 3.2 : 6.1) + vfx.next() * (cleave ? 6.5 : fizzle ? 2.6 : 4.5);
      const out = collapse ? radius * 0.75 : radius * 0.35;
      this.write(
        shards, i,
        x + Math.cos(a) * out, y + Math.sin(a) * out,
        Math.cos(a) * sp * spread, Math.sin(a) * sp * spread,
        fizzle ? 0.34 : 0.45, radius * (0.55 + vfx.next() * 0.5),
        hueToken, collapse ? MOTION_INWARD : MOTION_SHARD, 2.6, true,
        radius * 0.12,
      );
    }

    const shock = this.pools[POOL_SHOCK];
    // A fizzle skips the ring outright — the beat that says "that one barely mattered".
    if (!fizzle) {
      const si = this.claim(shock, true, vfx);
      if (si >= 0) {
        const rr = ringRadius * (cleave ? 1.25 : collapse ? 0.8 : 1);
        this.write(shock, si, x, y, 0, 0, collapse ? 0.24 : 0.18, rr, coreToken, MOTION_SHOCK, 2.2, true, 0.055);
      }
    }
  }

  /**
   * Three primitives the player's death beat is built from (`render/player-death.ts`).
   *
   * They exist here rather than there because `write` and `claim` are this class's
   * business: the pools, the §14.6 exemption flag and the ring buffer's recycling rule
   * are not something another module should be reaching into. Each is one pool write.
   */
  emitCore(x: number, y: number, size: number, life: number, color: number, vfx: RngStream): void {
    const pool = this.pools[POOL_EXPLOSION];
    const i = this.claim(pool, true, vfx);
    if (i >= 0) this.write(pool, i, x, y, 0, 0, life, size, color, MOTION_BALLISTIC, 3.6, true);
  }

  emitRing(x: number, y: number, radius: number, life: number, color: number, vfx: RngStream): void {
    const pool = this.pools[POOL_SHOCK];
    const i = this.claim(pool, true, vfx);
    if (i >= 0) this.write(pool, i, x, y, 0, 0, life, radius, color, MOTION_SHOCK, 2.4, true, 0.05);
  }

  emitEmber(x: number, y: number, vx: number, vy: number, life: number, color: number, vfx: RngStream): void {
    const pool = this.pools[POOL_SHARD];
    const i = this.claim(pool, true, vfx);
    if (i >= 0) this.write(pool, i, x, y, vx, vy, life, 0.16, color, MOTION_SHARD, 2.6, true, 0.05);
  }

  /** Ambient trench motes. Purely decorative: halved at rung 1, gone under pressure. */
  emitAmbient(
    camX: number,
    camY: number,
    viewW: number,
    viewH: number,
    dt: number,
    color: number,
    vfx: RngStream,
  ): void {
    const pool = this.pools[POOL_AMBIENT];
    const target = 22 * quality.ambientScale * quality.decorativeRate;
    let n = Math.floor(target * dt);
    if (vfx.next() < target * dt - n) n++;
    for (let k = 0; k < n; k++) {
      const i = this.claim(pool, false, vfx);
      if (i < 0) return;
      this.write(
        pool, i,
        camX + (vfx.next() - 0.5) * viewW * 1.1,
        camY + (vfx.next() - 0.5) * viewH * 1.1,
        (vfx.next() - 0.5) * 0.5,
        0.25 + vfx.next() * 0.5,
        2.4 + vfx.next() * 2.4,
        0.05 + vfx.next() * 0.05,
        color, MOTION_TWINKLE, 0.9, false,
      );
    }
  }

  /**
   * Status loop particles (§11.2.5 shape codes). Gameplay-cued: a status cue is in the
   * §14.6 exempt set and is never dropped or thinned.
   */
  emitStatus(
    x: number,
    y: number,
    radius: number,
    color: number,
    motion: number,
    vfx: RngStream,
    count = 1,
  ): void {
    const pool = this.pools[POOL_STATUS];
    for (let k = 0; k < count; k++) {
      const i = this.claim(pool, true, vfx);
      if (i < 0) return;
      const a = vfx.next() * Math.PI * 2;
      const r = radius * (0.4 + vfx.next() * 0.7);
      let vx = (vfx.next() - 0.5) * 0.6;
      let vy = 1.0;
      if (motion === MOTION_FALL) vy = -0.6;
      else if (motion === MOTION_BALLISTIC) {
        vx = Math.cos(a) * 3.2;
        vy = Math.sin(a) * 3.2;
      } else if (motion === MOTION_INWARD) {
        vx = -Math.cos(a) * 1.4;
        vy = -Math.sin(a) * 1.4;
      }
      this.write(
        pool, i,
        x + Math.cos(a) * r, y + Math.sin(a) * r,
        vx, vy,
        motion === MOTION_BALLISTIC ? 0.12 : 0.5 + vfx.next() * 0.4,
        0.1 + vfx.next() * 0.05,
        color, motion, 1.4, true,
      );
    }
  }

  /** Integrates every pool. `dt` is sim-derived seconds. */
  update(dt: number): void {
    this.droppedThisFrame = 0;
    if (dt <= 0) return;
    for (let p = 0; p < POOL_COUNT; p++) {
      const pool = this.pools[p];
      let alive = 0;
      for (let i = 0; i < pool.capacity; i++) {
        const l = pool.life[i];
        if (l <= 0) continue;
        const nl = l - dt;
        if (nl <= 0) {
          pool.life[i] = 0;
          continue;
        }
        pool.life[i] = nl;
        const m = pool.motion[i];
        if (m === MOTION_SHOCK) {
          alive++;
          continue;
        }
        if (m === MOTION_BALLISTIC || m === MOTION_SHARD) {
          const drag = Math.exp((m === MOTION_SHARD ? -2.4 : -5.5) * dt);
          pool.vx[i] *= drag;
          pool.vy[i] *= drag;
        } else if (m === MOTION_RISE) {
          pool.vy[i] += 1.6 * dt;
        } else if (m === MOTION_FALL) {
          pool.vy[i] -= 2.2 * dt;
        } else if (m === MOTION_INWARD) {
          pool.vx[i] *= Math.exp(-1.2 * dt);
          pool.vy[i] *= Math.exp(-1.2 * dt);
        }
        pool.x[i] += pool.vx[i] * dt;
        pool.y[i] += pool.vy[i] * dt;
        alive++;
      }
      pool.alive = alive;
    }
  }

  /** Writes every live particle into its batch. Ascending slot order, zero allocation. */
  sync(): void {
    for (let p = 0; p < POOL_COUNT; p++) {
      const pool = this.pools[p];
      const batch = this.batches[p];
      batch.begin();
      for (let i = 0; i < pool.capacity; i++) {
        const l = pool.life[i];
        if (l <= 0) continue;
        const t = l / pool.maxLife[i];
        const m = pool.motion[i];
        if (m === MOTION_SHOCK) {
          // Radius sweeps outward while the alpha runs 0.9 → 0 (AMD-6 D7).
          const grow = 1 - t;
          const r = Math.max(1e-3, pool.size[i] * (0.08 + 0.92 * grow * (2 - grow)) * 0.5);
          batch.push(pool.x[i], pool.y[i], 0, r, r, pool.color[i], 0.9 * t, pool.emissive[i], pool.size2[i], 0);
          continue;
        }
        if (m === MOTION_SHARD) {
          // Beam geometry runs x 0..1, so pointing it backwards puts the bright head at
          // the shard and the taper behind it — the 4-sample trail, for free.
          const rot = Math.atan2(-pool.vy[i], -pool.vx[i]);
          const len = Math.max(1e-3, pool.size[i] * (0.35 + 0.65 * t));
          // AMD-7 R2a: shards take the beam taper, so each one narrows and fades along
          // its own length instead of ending as a cut bar.
          batch.push(pool.x[i], pool.y[i], rot, len, Math.max(1e-3, pool.size2[i] * t), pool.color[i], t * t, pool.emissive[i], 0, 1);
          continue;
        }
        // Ease-out alpha with a short bright head; a linear fade reads as a smear.
        const alpha = t * t * (3 - 2 * t);
        const s = pool.size[i] * (0.6 + 0.4 * t);
        // AMD-7 R2b: stretch along velocity, minor axis unchanged.
        const vx = pool.vx[i];
        const vy = pool.vy[i];
        const speed = Math.sqrt(vx * vx + vy * vy);
        if (speed > 0.35) {
          const k = speed / STRETCH_REF_SPEED;
          const stretch = 1 + (k > STRETCH_MAX ? STRETCH_MAX : k);
          batch.push(pool.x[i], pool.y[i], Math.atan2(vy, vx), s * stretch, s, pool.color[i], alpha, pool.emissive[i], 0, 0);
          continue;
        }
        batch.push(pool.x[i], pool.y[i], 0, s, s, pool.color[i], alpha, pool.emissive[i], 0, 0);
      }
      batch.end();
    }
  }

  /** Drops every particle. Used on run reset and before a deterministic capture. */
  clear(): void {
    for (let p = 0; p < POOL_COUNT; p++) {
      const pool = this.pools[p];
      pool.life.fill(0);
      pool.alive = 0;
      pool.cursor = 0;
    }
  }

  dispose(): void {
    for (const b of this.batches) b.dispose();
  }
}
